export type LivesRulesBase = {
  livesLost: number;
  numDrivers: number;
};

export type LivesRules =
  | DriverLivesRules
  | RemainingLivesRules
  | TrackLivesRules;

export type DriverLivesRules = LivesRulesBase & {
  tillLeft: number;
};

export type RemainingLivesRules = LivesRulesBase & {
  tillRemaining: number;
};

export type TrackLivesRules = LivesRulesBase & {
  startTrack: number;
  endTrack: number | undefined;
};

export const isDriverLivesRules = (
  rules: LivesRules
): rules is DriverLivesRules => {
  return (rules as DriverLivesRules).tillLeft !== undefined;
};

export const isRemainingLivesRules = (
  rules: LivesRules
): rules is RemainingLivesRules => {
  return (rules as RemainingLivesRules).tillRemaining !== undefined;
};

export const matchesDriverLivesRules = (
  rule: DriverLivesRules,
  numDriversLeft: number
) => {
  return rule.tillLeft < numDriversLeft;
};

export const matchesRemainingLivesRules = (
  rule: RemainingLivesRules,
  numLivesLeft: number
) => {
  return rule.tillRemaining < numLivesLeft;
};

export const matchesTrackLivesRules = (
  rule: TrackLivesRules,
  trackNum: number
) => {
  if (!rule.endTrack) {
    return rule.startTrack === trackNum;
  }
  return rule.startTrack <= trackNum && rule.endTrack >= trackNum;
};

export const matchesLivesRules = (
  rule: LivesRules,
  numDriversLeft: number,
  numLivesLeft: number,
  trackNum: number
) => {
  if (isDriverLivesRules(rule)) {
    return matchesDriverLivesRules(rule, numDriversLeft);
  }

  if (isRemainingLivesRules(rule)) {
    return matchesRemainingLivesRules(rule, numLivesLeft);
  }

  return matchesTrackLivesRules(rule, trackNum);
};

export const getMatchingLivesRules = (
  rules: LivesRules[],
  numDriversLeft: number,
  numLivesLeft: number,
  trackNum: number
) => {
  return rules.find((r) =>
    matchesLivesRules(r, numDriversLeft, numLivesLeft, trackNum)
  );
};

export const getLivesLost = (
  rules: LivesRules,
  numDrivers: number,
  position: number
) => {
  const bestWinningPos = numDrivers - rules.numDrivers;
  if (position > bestWinningPos) {
    return rules.livesLost;
  }

  return 0;
};
